import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ShieldCheck,
    Star,
    MessageCircle,
    User,
    Clock,
    ArrowRight,
    Search,
    AlertTriangle,
    ThumbsUp,
    CheckCircle2,
    Check
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { useInfiniteTickets } from '../../hooks/useInfiniteTickets';

const QualityControl = () => {
    const { tickets, archiveTicketQC } = useTickets();
    const { user } = useAuth();
    const navigate = useNavigate();
    // filteredTickets replaced by hook
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        rating: 'all',
        reason: 'all',
        city: 'all',
        order: 'newest',
        status: 'pending' // New status filter
    });

    // Dynamic Cities List
    const availableCities = React.useMemo(() => {
        const cities = new Set(['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL']);
        if (tickets) {
            tickets.forEach(t => {
                if (t.city) cities.add(t.city);
            });
        }
        return Array.from(cities).sort().map(c => ({ value: c, label: c }));
    }, [tickets]);



    // Auto-refresh logic removed (handled by Firestore)

    const {
        tickets: displayedTickets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        refresh
    } = useInfiniteTickets({
        queryBuilder: (query) => {
            // BASE LOGIC: Has Feedback
            query = query.not('feedback', 'is', null);

            // 0. Status (Archived vs Pending) - DISABLED: is_archived_qc column doesn't exist
            // if (filters.status === 'pending') {
            //     query = query.not('is_archived_qc', 'is', true);
            // } else if (filters.status === 'analyzed') {
            //     query = query.is('is_archived_qc', true);
            // }

            // Filters
            if (filters.city !== 'all') query = query.eq('city', filters.city);

            // Rating Filter - tricky on JSONB. 
            // If rating is numeric in JSON, we can try ->> but casting in filter is hard.
            // We will rely on loading the feedbacks and letting user filter visually if the server filter fails?
            // Actually, for now, let's just filter by City/Status/Search SERVER SIDE basics
            // And let strict client logic apply to what we fetch? No, that hides data.
            // We will fetch ALL feedback tickets (paginated) and show them. The "Strict Mode" is replaced by seeing all feedback.

            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                // Search in feedback content or user info
                // feedback->>comment ilike 
                // query = query.or(`title.ilike.%${term}%,id.ilike.%${term}%,feedback->>comment.ilike.%${term}%`); (syntax approx)
                // Let's stick to safe text fields
                query = query.or(`title.ilike.%${term}%,id.ilike.%${term}%,author_name.ilike.%${term}%`);
            }

            if (filters.order === 'newest') {
                query = query.order('created_at', { ascending: false });
            } else {
                query = query.order('created_at', { ascending: true });
            }

            return query;
        },
        pageSize: 20
    }, [filters, searchTerm]);

    // Apply strict filtering CLIENT SIDE to the PAGE we loaded?
    // No, pagination works best when we show what we load.
    // The "Strict Mode" was hiding "Good service, 5 stars, no comment" tickets.
    // Now we will show them all, which is better for "Optimizing".
    // We can highlight them visually.

    const handleFinalize = async (e, ticketId) => {
        e.stopPropagation(); // Prevent card click
        if (!user) return;

        toast.promise(archiveTicketQC(ticketId, user), {
            loading: 'Finalizando análise...',
            success: 'Feedback analisado com sucesso!',
            error: 'Erro ao finalizar.'
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getQCReason = (feedback) => {
        const rating = feedback.rating || 0;
        const staffRating = feedback.staffRating || 0;
        const commentLength = (feedback.comment || '').length;

        if ((rating > 0 && rating <= 3) || (staffRating > 0 && staffRating <= 3)) {
            return {
                label: 'Baixa Avaliação',
                color: 'text-red-400',
                bg: 'bg-red-500/10',
                icon: AlertTriangle
            };
        }
        if (rating === 0 && commentLength > 0) {
            return {
                label: 'Feedback Textual',
                color: 'text-yellow-400',
                bg: 'bg-yellow-500/10',
                icon: MessageCircle
            };
        }
        if (rating === 5 && commentLength > 300) {
            return {
                label: 'Elogio Detalhado',
                color: 'text-emerald-400',
                bg: 'bg-emerald-500/10',
                icon: ThumbsUp
            };
        }
        if (commentLength > 500) {
            return {
                label: 'Feedback Extenso',
                color: 'text-blue-400',
                bg: 'bg-blue-500/10',
                icon: MessageCircle
            };
        }
        return {
            label: 'Feedback',
            color: 'text-gray-400',
            bg: 'bg-gray-500/10',
            icon: ShieldCheck
        };
    };

    const FilterDropdown = ({ label, value, onChange, options, icon: Icon }) => (
        <div className="flex flex-col gap-2 flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-widest flex items-center gap-1.5 pl-1">
                {Icon && <Icon className="w-3 h-3 text-blue-400/70" />}
                {label}
            </label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="w-full bg-white/5 border-white/10 text-gray-200 h-10 rounded-xl hover:bg-white/10 hover:border-blue-500/30 hover:text-white transition-all focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-900/95 border-gray-800 backdrop-blur-xl text-gray-200">
                    {options.map(opt => (
                        <SelectItem
                            key={opt.value}
                            value={opt.value}
                            className="focus:bg-blue-600/20 focus:text-blue-200 cursor-pointer"
                        >
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div >
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <ShieldCheck className="h-8 w-8 text-blue-500" />
                        Controle de Qualidade
                    </h1>
                    <p className="text-gray-400 mt-1">Análise de feedbacks críticos e detalhados dos usuários</p>
                </div>

                {/* AREA DE FILTROS REFORMULADA */}
                <div className="bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-xl rounded-[2rem] p-6 border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                    <div className="flex flex-col gap-6 relative z-10">
                        {/* Search Bar - Top Full Width */}
                        <div className="relative w-full">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-blue-400" />
                            </div>
                            <Input
                                type="text"
                                placeholder="Buscar nos feedbacks (ID, Nome, Conteúdo)..."
                                className="pl-11 h-12 bg-white/5 border-white/10 text-white focus:border-blue-500/50 rounded-2xl transition-all shadow-inner focus:bg-white/[0.07] text-sm font-medium placeholder:text-gray-600"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Filters Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <FilterDropdown
                                label="Status"
                                value={filters.status}
                                onChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                                options={[
                                    { value: 'all', label: 'Todos' },
                                    { value: 'pending', label: 'Pendentes' },
                                    { value: 'analyzed', label: 'Analisados' }
                                ]}
                                icon={CheckCircle2}
                            />
                            <FilterDropdown
                                label="Avaliação"
                                value={filters.rating}
                                onChange={(v) => setFilters(prev => ({ ...prev, rating: v }))}
                                options={[
                                    { value: 'all', label: 'Todas' },
                                    { value: '1', label: '1 Estrela' },
                                    { value: '2', label: '2 Estrelas' },
                                    { value: '3', label: '3 Estrelas' },
                                    { value: '4', label: '4 Estrelas' },
                                    { value: '5', label: '5 Estrelas' }
                                ]}
                                icon={Star}
                            />
                            <FilterDropdown
                                label="Motivo"
                                value={filters.reason}
                                onChange={(v) => setFilters(prev => ({ ...prev, reason: v }))}
                                options={[
                                    { value: 'all', label: 'Todos' },
                                    { value: 'Baixa Avaliação', label: 'Baixa Avaliação' },
                                    { value: 'Elogio Detalhado', label: 'Elogio Detalhado' },
                                    { value: 'Feedback Extenso', label: 'Feedback Extenso' }
                                ]}
                                icon={AlertTriangle}
                            />
                            <FilterDropdown
                                label="Cidade"
                                value={filters.city}
                                onChange={(v) => setFilters(prev => ({ ...prev, city: v }))}
                                options={[
                                    { value: 'all', label: 'Todas' },
                                    ...availableCities
                                ]}
                                icon={ArrowRight}
                            />
                            <FilterDropdown
                                label="Ordem"
                                value={filters.order}
                                onChange={(v) => setFilters(prev => ({ ...prev, order: v }))}
                                options={[
                                    { value: 'newest', label: 'Mais Recentes' },
                                    { value: 'oldest', label: 'Mais Antigos' }
                                ]}
                                icon={Clock}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-4">
                {displayedTickets.length > 0 ? (
                    <>
                        {displayedTickets.map((ticket) => {
                            const reason = getQCReason(ticket.feedback);
                            const ReasonIcon = reason.icon;

                            return (
                                <motion.div
                                    key={ticket.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    whileHover={{ scale: 1.005 }}
                                >
                                    <Card
                                        className={`bg-[#0F0F10] border transition-all cursor-pointer group relative overflow-hidden ${reason.label === 'Baixa Avaliação' ? 'border-red-500/20 hover:border-red-500/40' :
                                            reason.label === 'Elogio Detalhado' ? 'border-emerald-500/20 hover:border-emerald-500/40' :
                                                'border-blue-500/20 hover:border-blue-500/40'
                                            }`}
                                        onClick={() => navigate(`/staff/tickets/${ticket.id}`)}
                                    >
                                        {/* Gradient Accent Background */}
                                        <div className={`absolute inset-0 bg-gradient-to-br opacity-5 pointer-events-none ${reason.label === 'Baixa Avaliação' ? 'from-red-500 to-transparent' :
                                            reason.label === 'Elogio Detalhado' ? 'from-emerald-500 to-transparent' :
                                                'from-blue-500 to-transparent'
                                            }`} />

                                        <CardContent className="p-4 relative z-10">
                                            {/* HEADER: User Info & Meta */}
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {/* Avatar */}
                                                    <div className="w-10 h-10 rounded-full bg-gray-800 border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                                                        {ticket.authorAvatar ? (
                                                            <img src={ticket.authorAvatar} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <User className="w-5 h-5 text-gray-500" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="text-sm font-bold text-white leading-tight truncate">
                                                                {ticket.formData?.characterName || ticket.authorName || 'Usuário Desconhecido'}
                                                            </h3>
                                                            <Badge variant="outline" className="border-white/10 text-[10px] text-gray-400 h-4 px-1.5 uppercase font-medium">
                                                                {ticket.city || 'Global'}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                                            <span className="font-mono text-gray-600">#{ticket.id}</span>
                                                            <span className="w-0.5 h-0.5 rounded-full bg-gray-600" />
                                                            <span className="uppercase tracking-wide truncate max-w-[150px]">
                                                                {ticket.category ? ticket.category.replace(/_/g, ' ') : 'Geral'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <Badge className={`${reason.bg} ${reason.color} border-none px-2 py-0.5 rounded-md uppercase text-[9px] font-black tracking-widest shadow-lg shrink-0`}>
                                                    <ReasonIcon className="w-3 h-3 mr-1.5" />
                                                    {reason.label}
                                                </Badge>
                                            </div>

                                            {/* INFO ROW: Staff Attributed */}
                                            <div className="flex items-center gap-2 mb-3 bg-white/[0.02] px-3 py-1.5 rounded-lg border border-white/5">
                                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider shrink-0">Atendimento:</span>
                                                {ticket.assignees && ticket.assignees.length > 0 ? (
                                                    <div className="flex items-center gap-2 overflow-hidden">
                                                        {ticket.assignees.map((staff, idx) => (
                                                            <div key={idx} className="flex items-center gap-1.5">
                                                                <div className="w-4 h-4 rounded-full bg-gray-700 overflow-hidden">
                                                                    {staff.avatar && <img src={`https://cdn.discordapp.com/avatars/${staff.discordId}/${staff.avatar}.png`} className="w-full h-full object-cover" alt="" />}
                                                                </div>
                                                                <span className="text-xs text-gray-300 font-medium truncate">{staff.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-600 italic">Não atribuído</span>
                                                )}
                                            </div>

                                            {/* BODY: Rating & Comment (Compact) */}
                                            <div className="bg-black/20 rounded-lg p-3 border border-white/5 flex flex-col gap-2">
                                                {/* Stars */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-0.5">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <Star
                                                                key={star}
                                                                className={`w-3.5 h-3.5 ${star <= (ticket.feedback?.rating || 0)
                                                                    ? 'text-yellow-400 fill-yellow-400'
                                                                    : 'text-gray-800'
                                                                    }`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                                        {formatDate(ticket.feedback?.createdAt || ticket.lastUpdate)}
                                                    </span>
                                                </div>

                                                {/* Comment */}
                                                {ticket.feedback?.comment && (
                                                    <p className="text-gray-200 text-xs leading-relaxed line-clamp-3 italic border-l-2 border-white/10 pl-2">
                                                        "{ticket.feedback.comment}"
                                                    </p>
                                                )}
                                            </div>

                                            {/* FOOTER: Minimal Meta & Actions */}
                                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/5">

                                                {/* Status Badge or Action Button */}
                                                {ticket.isArchivedQC ? (
                                                    <div className="flex items-center gap-1.5 bg-green-500/10 px-2 py-1 rounded-md border border-green-500/20">
                                                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                                                        <div className="flex flex-col">
                                                            <span className="text-[8px] font-black text-green-500 uppercase tracking-widest leading-none">Concluído por</span>
                                                            <span className="text-[9px] font-bold text-green-300 uppercase leading-none mt-0.5">{ticket.qcAnalyzedBy || 'Staff'}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        onClick={(e) => handleFinalize(e, ticket.id)}
                                                        size="sm"
                                                        className="bg-white/5 hover:bg-green-500/20 hover:text-green-400 text-gray-400 border border-white/10 hover:border-green-500/40 h-7 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all"
                                                    >
                                                        <Check className="w-3 h-3 mr-1" /> Analisado / Finalizar
                                                    </Button>
                                                )}

                                                <div className="flex items-center gap-1 text-[10px] text-gray-500 font-medium uppercase tracking-wider group-hover:text-blue-400 transition-colors ml-auto">
                                                    <span>Ver Detalhes</span>
                                                    <ArrowRight className="w-3 h-3" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                        {hasMore && (
                            <div className="flex justify-center pt-4 pb-8">
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                                >
                                    {loadingMore ? 'Carregando...' : 'Carregar Mais'}
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    !loading && (
                        <div className="text-center py-20 bg-gray-900/20 rounded-3xl border border-dashed border-gray-800">
                            <ShieldCheck className="h-16 w-16 text-gray-700 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-white mb-2">Sem alertas de qualidade</h3>
                            <p className="text-gray-400 max-w-sm mx-auto">
                                Não há tickets que atendam aos critérios de controle de qualidade no momento.
                            </p>
                        </div>
                    )
                )}
                {loading && displayedTickets.length === 0 && (
                    <div className="text-center py-20 text-gray-400">Carregando feedbacks...</div>
                )}
            </div>
        </div>
    );
};

export default QualityControl;
